import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, Subscription } from 'rxjs';
import { MenuService } from './menu/menu.service';
import { ScrollService } from './scroll/scroll.service';
import { LocalStorageService } from './local-storage/local-storage.service';
import { environment } from '../../../environments/environment';
import { AppSettings } from '../interfaces/app-settings.interface';
import { normalizeLang, langToRoute, routeToLang, I18N_CONFIG } from '../localization/i18n.config';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private navSub?: Subscription;
  private readonly scrollDelayMs = 150;
  private readonly scrollTimeoutMs = 2000;

  constructor(
    private router: Router,
    private translate: TranslateService,
    private menuService: MenuService,
    private scrollService: ScrollService,
    private localStorageService: LocalStorageService,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {}

  init(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const windowRef = this.document.defaultView;
    if (!windowRef) return;

    const pathname = windowRef.location.pathname;
    const hash = windowRef.location.hash;
    const fragment = hash.replace('#', '');

    this.navSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const url = this.router.url;
        this.syncLanguageWithUrl(url);
        const navFragment = this.router.parseUrl(url).fragment;

        if (navFragment) {
          this.menuService.closeMenu();
          this.scrollToFragment(navFragment);
        }
      });

    // Redirect from root to preferred language path
    if (pathname === '/') {
      const settings = this.localStorageService
        .getItem<AppSettings>(environment.localStorageKey.appSettings);
      const route = `/${langToRoute(normalizeLang(settings?.language))}`;
      this.router.navigate([route], { fragment: fragment || undefined, replaceUrl: true });
      return;
    }

    this.syncLanguageWithUrl(`${pathname}${hash}`);

    if (fragment) {
      this.scrollWhenReady(fragment);
    }
  }

  destroy(): void {
    this.navSub?.unsubscribe();
  }

  private scrollToFragment(fragment: string): void {
    setTimeout(() => {
      this.scrollService.scrollToFragment(fragment, 'header', 52);
    }, this.scrollDelayMs);
  }

  private scrollWhenReady(fragment: string): void {
    const start = Date.now();
    const poll = () => {
      if (this.document.getElementById(fragment)) {
        this.scrollToFragment(fragment);
        return;
      }
      if (Date.now() - start < this.scrollTimeoutMs) {
        requestAnimationFrame(poll);
      }
    };
    requestAnimationFrame(poll);
  }

  private syncLanguageWithUrl(url: string): void {
    const [pathPart] = url.split('#');
    const path = (pathPart || '').toLowerCase();
    const nextLang = routeToLang(path);

    if (this.translate.currentLang !== nextLang) {
      this.translate.use(nextLang);
    }

    const settings = this.localStorageService
      .getItem<AppSettings>(environment.localStorageKey.appSettings);

    if (settings?.language !== nextLang) {
      const appSettings = settings || { language: I18N_CONFIG.defaultLang };
      appSettings.language = nextLang;
      this.localStorageService.setItem(environment.localStorageKey.appSettings, appSettings);
    }
  }
}

